import { type BreadcrumbItem, TopNavBar } from '@aecom-ai-engineering/react-components';
import { useLocalState } from '@consigli/hooks';
import clsx from 'clsx';
import { type FC, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { AppSideNav } from './app-side-nav';
import { SHELL_COLUMN, SHELL_GUTTERS } from './content-column';
import { useBreadcrumbNavigation } from './use-breadcrumb-navigation';

/** A preference, like `project-list.view`, so it outlives the page and the visit. */
const SIDE_NAV_COLLAPSED_KEY = 'app-shell.side-nav-collapsed';

/**
 * A flex column that fills the region, not a plain block: the frames centre a
 * page's empty and loading states in the space below the header, and a child
 * cannot ask for that with `flex-1` unless this is a flex parent with height to
 * give. It was a block, so those `flex-1`s resolved to nothing and the states
 * sat wherever their own padding put them. Content-height children are
 * unaffected — they do not grow.
 */
const CONTENT_COLUMN = `${SHELL_COLUMN} flex min-h-0 flex-1 flex-col`;

/**
 * The pinned chrome: the breadcrumb bar only, on every AppShell page. The
 * banner scrolls away with the page. Sticky resolves against the document,
 * which root-layout keeps scrollable by deliberately setting no overflow rule.
 *
 * z-60 mirrors the legacy PageHeader, so index.css's DocumentViewer lift to 70
 * covers this too.
 */
const TOP_BAR = 'sticky top-0 z-[60]';

interface AppShellProps {
  /** Handed straight to TopNavBar. The last item is the current page. */
  breadcrumbs: BreadcrumbItem[];
  /**
   * A page header spanning the content region, rendered above the content
   * column and outside its gutters, directly under the breadcrumb bar and
   * scrolling with the page. A child of `children` cannot place itself there:
   * the column's centring offset depends on the viewport, and the side nav
   * rules out a `50vw` break-out. Put its content back in
   * SHELL_GUTTERS/SHELL_COLUMN so it lines up with the body below.
   */
  banner?: ReactNode;
  children: ReactNode;
}

/** Side navigation, breadcrumb bar, and the content region a page renders into. */
export const AppShell: FC<AppShellProps> = ({ breadcrumbs, banner, children }) => {
  const { t } = useTranslation();

  // Every page mounts its own AppShell, so plain state reopened the nav on
  // each navigation. useLocalState hands back whatever JSON is stored,
  // unchecked, hence the strict comparison.
  const [storedCollapsed, setCollapsed] = useLocalState<boolean>(SIDE_NAV_COLLAPSED_KEY, false);
  const collapsed = storedCollapsed === true;
  const handleBreadcrumbClick = useBreadcrumbNavigation();

  return (
    // The design-system body font here rather than on `body`: the DS component
    // sheets are split between inheriting the family and hardcoding Switzer, so
    // setting it at this root makes the `inherit` half match the other half.
    <div className="ds-body-font flex flex-1">
      <AppSideNav collapsed={collapsed} />

      <div className="flex min-w-0 flex-1 flex-col">
        {/* sidebarLabel from the shell's state, not TopNavBar's own: without it
            the trigger is hardcoded English in all 11 locales. */}
        <TopNavBar
          breadcrumbs={breadcrumbs}
          className={TOP_BAR}
          onClick={handleBreadcrumbClick}
          onSidebarToggle={() => setCollapsed(!collapsed)}
          sidebarExpanded={!collapsed}
          sidebarLabel={collapsed ? t('navbar.side-nav.expand') : t('navbar.side-nav.collapse')}
        />

        {/* The design's content region is bg-primary; the nav and top bar keep
            their own fills, so this is scoped to the region that changes. */}
        <main className="flex flex-1 flex-col bg-[var(--ds-color-bg-primary)]">
          {banner}

          {/* Gutters here rather than on <main> so the banner sits outside
              them. Still a flex column with height to give, because
              CONTENT_COLUMN asks for `flex-1`.

              `pt-6` only without a banner: a banner brings its own top edge,
              and the body below its rule already brings `py-8`. */}
          <div
            className={clsx(
              'flex min-h-0 flex-1 flex-col pb-6',
              SHELL_GUTTERS,
              banner === undefined && 'pt-6',
            )}
          >
            <div className={CONTENT_COLUMN}>{children}</div>
          </div>
        </main>
      </div>
    </div>
  );
};
